import { useEffect, useState, useMemo } from 'react';
import { fitnessFetcher } from '@/lib/api/fitness-fetcher';
import type { BiometricsData } from '@/lib/api/fitness-fetcher';
import { useStore } from '@/lib/store';
import { Loader2, TrendingUp, TrendingDown, Minus, HelpCircle, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Metric {
    label: string;
    current: number | null;
    avg: number;
    unit?: string;
}

export function ReadinessWidget() {
    const intervalsCredentials = useStore(state => state.intervals);
    const consent = useStore(state => state.consent);
    const planSettings = useStore(state => state.planSettings);
    const [biometrics, setBiometrics] = useState<BiometricsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadWellness = async () => {
            const isConnected = intervalsCredentials?.accessToken && intervalsCredentials?.athleteId;
            if (!isConnected || !consent.health) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const wellness = await fitnessFetcher.fetchWellness(7);
                const calculated = fitnessFetcher.calculateBiometrics(wellness);
                setBiometrics(calculated);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load wellness data');
                console.error('[ReadinessWidget] Error:', err);
            } finally {
                setLoading(false);
            }
        };

        loadWellness();
    }, [intervalsCredentials, consent.health]);

    // Build list of available metrics (only non-null values)
    const availableMetrics = useMemo((): Metric[] => {
        if (!biometrics) return [];
        const metrics: Metric[] = [];

        if (biometrics.rhr.current !== null) {
            metrics.push({
                label: 'RHR',
                current: biometrics.rhr.current,
                avg: biometrics.rhr.avg,
                unit: 'bpm'
            });
        }

        if (biometrics.hrv.current !== null) {
            metrics.push({
                label: 'HRV',
                current: biometrics.hrv.current,
                avg: biometrics.hrv.avg,
                unit: 'ms'
            });
        }

        // Prefer sleepHours (actual duration) over sleep quality rating
        if (biometrics.sleepHours?.current !== null && biometrics.sleepHours?.current !== undefined) {
            metrics.push({
                label: 'Sleep',
                current: biometrics.sleepHours.current,
                avg: biometrics.sleepHours.avg,
                unit: 'hrs'
            });
        }

        if (biometrics.soreness.current !== null) {
            metrics.push({
                label: 'Soreness',
                current: biometrics.soreness.current,
                avg: biometrics.soreness.avg,
                unit: '/10'
            });
        }
        return metrics;
    }, [biometrics]);

    // Calculate readiness score (0-100) with personal benchmarks
    const { score: readinessScore, breakdown } = useMemo(() => {
        if (!biometrics || availableMetrics.length === 0) return { score: 0, breakdown: [] };

        const adjustments: string[] = [];
        let totalAdjustment = 0;

        // Baseline: 90 (optimized for feeling "Ready" by default)
        const baseline = 90;

        // RHR Logic: Benchmarked against 28-day average
        if (biometrics.rhr.current !== null && biometrics.rhr.avg > 0) {
            const deviation = (biometrics.rhr.current - biometrics.rhr.avg) / biometrics.rhr.avg;
            if (deviation > 0.08) {
                const penalty = Math.min(15, Math.round(deviation * 100));
                totalAdjustment -= penalty;
                adjustments.push(`RHR elevated by ${Math.round(deviation * 100)}% (-${penalty}%)`);
            } else if (deviation < -0.05) {
                totalAdjustment += 2;
                adjustments.push(`RHR lower than usual (+2%)`);
            }
        }

        // HRV Logic: Benchmarked against 28-day average
        if (biometrics.hrv.current !== null && biometrics.hrv.avg > 0) {
            const deviation = (biometrics.hrv.current - biometrics.hrv.avg) / biometrics.hrv.avg;
            if (deviation < -0.1) {
                const penalty = Math.min(15, Math.round(Math.abs(deviation) * 50));
                totalAdjustment -= penalty;
                adjustments.push(`HRV down ${Math.round(Math.abs(deviation) * 100)}% (-${penalty}%)`);
            } else if (deviation > 0.1) {
                totalAdjustment += 5;
                adjustments.push(`HRV high (+5%)`);
            }
        }

        // Sleep Logic: Personal baseline (28-day average)
        if (biometrics.sleepHours.current !== null && biometrics.sleepHours.avg > 0) {
            const diff = biometrics.sleepHours.current - biometrics.sleepHours.avg;
            if (diff < -0.5) {
                const penalty = Math.min(20, Math.round(Math.abs(diff) * 10));
                totalAdjustment -= penalty;
                adjustments.push(`Slept ${Math.abs(diff).toFixed(1)}h less than avg (-${penalty}%)`);
            } else if (diff >= 0.5) {
                totalAdjustment += 5;
                adjustments.push(`Slept more than usual (+5%)`);
            }
        }

        // Soreness Logic
        if (biometrics.soreness.current !== null && biometrics.soreness.current > 4) {
            const penalty = (biometrics.soreness.current - 4) * 5;
            totalAdjustment -= penalty;
            adjustments.push(`Body soreness ${biometrics.soreness.current}/10 (-${penalty}%)`);
        }

        if (adjustments.length === 0) {
            adjustments.push("Metrics are within your typical range.");
        }

        return {
            score: Math.max(0, Math.min(100, baseline + totalAdjustment)),
            breakdown: adjustments
        };
    }, [biometrics, availableMetrics]);

    const getTrendIcon = (current: number | null, avg: number, inverted: boolean = false) => {
        if (current === null) return null;
        const diff = current - avg;
        const threshold = avg * 0.05;

        if (Math.abs(diff) < threshold) {
            return <Minus className="w-3 h-3 text-gray-400" />;
        }

        const isUp = diff > 0;
        const isGood = inverted ? !isUp : isUp;
        return isGood ? (
            <TrendingUp className="w-3 h-3 text-green-500" />
        ) : (
            <TrendingDown className="w-3 h-3 text-red-500" />
        );
    };

    const hrvSuggestion = useMemo(() => {
        if (!planSettings?.hrvReadinessSuggestionsEnabled) {
            return null;
        }

        if (!biometrics || biometrics.hrv.current === null || biometrics.hrv.avg <= 0) {
            return null;
        }

        const deviation = (biometrics.hrv.current - biometrics.hrv.avg) / biometrics.hrv.avg;
        if (deviation <= -0.2) {
            return {
                tone: 'high',
                text: 'Suggestion only: HRV is well below your usual range today. Consider swapping intensity for recovery or easy aerobic work.',
            } as const;
        }

        if (deviation <= -0.1) {
            return {
                tone: 'moderate',
                text: 'Suggestion only: HRV is below your normal range today. Consider easing the session if it feels heavy once you start.',
            } as const;
        }

        return null;
    }, [biometrics, planSettings?.hrvReadinessSuggestionsEnabled]);

    if (loading) {
        return (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 shadow-sm h-[180px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!consent.health) {
        return (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-zinc-800 h-full flex flex-col justify-center">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Readiness</span>
                    <ShieldCheck className="w-3 h-3 text-muted-foreground/30" />
                </div>
                <div className="text-center space-y-2 py-4">
                    <p className="text-[10px] text-muted-foreground leading-snug">
                        Enable <span className="font-bold">Health Data</span> consent in Settings to calculate your readiness score from Heart Rate and HRV.
                    </p>
                </div>
            </div>
        );
    }

    if (error || availableMetrics.length === 0) {
        return (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Wellness</span>
                    <span className="text-xs text-muted-foreground">No data</span>
                </div>
                <p className="text-[10px] text-muted-foreground py-4 text-center">
                    {error || 'Wellness sync pending...'}
                </p>
            </div>
        );
    }

    // Circular Progress Constants
    const size = 52;
    const strokeWidth = 5;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (readinessScore / 100) * circumference;

    const scoreColor = readinessScore >= 80 ? '#22c55e' : readinessScore >= 65 ? '#eab308' : '#ef4444';

    return (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-3 shadow-sm flex flex-col h-full border border-gray-100 dark:border-zinc-800">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">Readiness</span>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <HelpCircle className="w-3 h-3 text-muted-foreground/30 hover:text-muted-foreground transition-colors" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[220px] p-3 bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 shadow-xl">
                                <div className="space-y-2">
                                    <div className="pb-1.5 border-b border-gray-100 dark:border-zinc-800">
                                        <p className="font-bold text-[11px] mb-1">Formula</p>
                                        <code className="text-[10px] text-primary font-mono bg-secondary/30 px-1.5 py-0.5 rounded">
                                            Readiness = 90% + Σ Adjustments
                                        </code>
                                    </div>
                                    <div className="pb-1.5 border-b border-gray-100 dark:border-zinc-800">
                                        <p className="font-bold text-[10px] text-amber-600 dark:text-amber-500 mb-0.5 flex items-center gap-1">
                                            <AlertTriangle className="w-2.5 h-2.5" />
                                            NOT MEDICAL ADVICE
                                        </p>
                                        <p className="text-[9px] text-muted-foreground leading-tight italic">
                                            This score is for athletic performance tracking only. It is not intended to diagnose or treat any medical condition.
                                        </p>
                                    </div>
                                    <p className="text-[10px] leading-relaxed text-muted-foreground">
                                        Baseline is 90% (Fresh). Adjustments are calculated from deviations against your 4-week personal averages.
                                    </p>
                                    <ul className="space-y-1 text-[10px]">
                                        {breakdown.map((item, i) => (
                                            <li key={i} className="flex items-start gap-1.5 text-primary/90">
                                                <span className="text-muted-foreground mt-0.5">•</span>
                                                <span className="leading-tight">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <span className="text-[9px] font-semibold text-muted-foreground/40 uppercase tracking-tighter">4-Week AVG</span>
            </div>

            <div className="flex items-center gap-3 justify-between flex-1">
                {/* Circular Score */}
                <div className="relative flex-shrink-0">
                    <svg width={size} height={size} className="transform -rotate-90">
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="transparent"
                            stroke="currentColor"
                            strokeWidth={strokeWidth}
                            className="text-secondary/10"
                        />
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="transparent"
                            stroke={scoreColor}
                            strokeWidth={strokeWidth}
                            strokeDasharray={circumference}
                            style={{ strokeDashoffset: offset }}
                            strokeLinecap="round"
                            className="transition-all duration-1000 ease-out"
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-black tracking-tighter">{readinessScore}%</span>
                    </div>
                </div>

                {/* Vertical Metrics List - Compact */}
                <div className="flex-1 space-y-1.5">
                    {availableMetrics.slice(0, 3).map((metric) => {
                        const isInverted = metric.label === 'RHR' || metric.label === 'Soreness';
                        return (
                            <div key={metric.label} className="flex items-center justify-between text-[9px]">
                                <div className="flex items-center gap-1">
                                    <span className="font-bold text-muted-foreground/60 uppercase tracking-tight">{metric.label}</span>
                                    {getTrendIcon(metric.current, metric.avg, isInverted)}
                                </div>
                                <div className="flex items-baseline gap-0.5">
                                    <span className="font-bold text-primary">{metric.current}</span>
                                    <span className="text-muted-foreground/40 font-medium">/{metric.avg}{metric.unit === 'hrs' ? 'h' : ''}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {hrvSuggestion && (
                <div
                    className={`mt-2 rounded-xl border px-2.5 py-2 text-[9px] leading-snug ${
                        hrvSuggestion.tone === 'high'
                            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300'
                            : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300'
                    }`}
                >
                    {hrvSuggestion.text} Use context too: life stress, sleep, travel, and how you feel still matter.
                </div>
            )}

            {/* Micro Disclaimer */}
            <p className="mt-2 text-[7.5px] text-muted-foreground/30 italic text-center leading-none">
                Score is a guide — perceived readiness matters most. <br />
                <span className="font-bold not-italic">NOT MEDICAL ADVICE.</span>
            </p>
        </div>
    );
}


